import React from "react";
import { Progress } from "antd";

export const UNLIMITED_LABEL = "Unlimited";
export const USAGE_TRAIL_COLOR = "#DCDFEC";
export const USAGE_OVER_CAP_COLOR = "#D83A52";

interface UsageRingProps {
  /** Usage against the plan cap, 0–100+. Ignored when `unlimited` is set. */
  percent: number;
  /** Ring colour while under the cap. */
  color: string;
  size: number;
  strokeWidth?: number;
  /** Enterprise / promo accounts: no cap, so nothing to fill. */
  unlimited?: boolean;
  /** Shown in the middle of a metered ring, e.g. the count. Omit for a bare ring. */
  label?: React.ReactNode;
}

/**
 * Circle usage meter shared by the item view strip and the settings card.
 *
 * Metered plans fill the ring against the cap and turn red at 100%. Unlimited
 * plans keep the ring in place so nothing shifts in the layout, but draw only
 * the trail with an infinity mark inside: a full ring would read as "at
 * capacity" and an empty one as "nothing used", and neither is true.
 */
const UsageRing = ({
  percent,
  color,
  size,
  strokeWidth = 10,
  unlimited = false,
  label,
}: UsageRingProps) => {
  if (unlimited) {
    return (
      <Progress
        type="circle"
        percent={0}
        size={size}
        trailColor={USAGE_TRAIL_COLOR}
        strokeWidth={strokeWidth}
        format={() => (
          <span
            role="img"
            aria-label={UNLIMITED_LABEL}
            className="!font-figtree font-medium"
            style={{ fontSize: Math.round(size * 0.3), color: "var(--secondary-text-color)" }}
          >
            ∞
          </span>
        )}
      />
    );
  }

  const overCap = percent >= 100;

  return (
    <Progress
      type="circle"
      percent={percent}
      size={size}
      trailColor={USAGE_TRAIL_COLOR}
      showInfo={label !== undefined}
      format={() => (
        // antd tints the text red once the ring is in its "exception" state; the
        // count stays in the normal text colour and only the ring signals the cap.
        <span
          className="!font-figtree font-semibold text-[var(--primary-text-color)]"
          style={{ fontSize: Math.round(size * 0.32) }}
        >
          {label}
        </span>
      )}
      strokeColor={overCap ? USAGE_OVER_CAP_COLOR : color}
      status={overCap ? "exception" : "normal"}
      strokeWidth={strokeWidth}
    />
  );
};

export default UsageRing;
